// ../components/MondayMeetingsList.tsx
import LazyImage from "./ui/LazyImage";
import { Play, Pause } from "lucide-react";

type Track = {
  id: number;
  title: string;
  subtitle: string;
  src?: string;
  cover?: string | null;
  durationLabel?: string;
  date?: string;
};

type MondayMeetingsListProps = {
  tracks: Track[];
  currentTrackId?: number;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onSelectTrack: (trackId: number) => void;
};

export default function MondayMeetingsList({
  tracks,
  currentTrackId,
  isPlaying,
  onTogglePlay,
  onSelectTrack,
}: MondayMeetingsListProps) {
  if (tracks.length === 0) {
    return (
      <p className="text-right text-green-05" dir="rtl">
        هیچ جلسه‌ای یافت نشد.
      </p>
    );
  }

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex flex-col gap-3">
        {tracks.map((track) => {
          const isActive = currentTrackId === track.id;

          return (
            <div
              key={track.id}
              onClick={() => {
                if (isActive) {
                  onTogglePlay();
                } else {
                  onSelectTrack(track.id);
                }
              }}
              className={`group flex items-center justify-between p-4 border border-white cursor-pointer rounded-[20px] ${
                isActive
                  ? "bg-green-04 text-white"
                  : "hover:shadow-sm bg-white hover:bg-green-02 hover:border hover:border-green-02.5 transition-all duration-300"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="relative size-16 xl:size-[72px] rounded-[20px] overflow-hidden flex-shrink-0 bg-gray-100 select-none">
                  {track.cover && (
                    <LazyImage
                      src={track.cover}
                      alt={track.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  )}
                  <div
                    className={`absolute inset-0 flex items-center justify-center transition-colors ${
                      isActive
                        ? "bg-black/40"
                        : "bg-black/25 group-hover:bg-black/40"
                    }`}
                  >
                    <div className="text-white/70 border-2 border-white/70 rounded-xl p-1.5 drop-shadow-md transition-transform duration-200 group-hover:scale-110">
                      {isActive && isPlaying ? (
                        <Pause size={14} className="fill-current" />
                      ) : (
                        <Play size={14} className="fill-current" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-right space-y-1">
                  <h4
                    className={`text-sm xl:text-base font-bold transition-colors ${
                      isActive ? "text-gray-100" : "text-green-04"
                    }`}
                  >
                    {track.title}
                  </h4>
                  <p
                    className={`text-xs line-clamp-1 font-normal${
                      isActive ? "text-white/50" : "text-green-04"
                    }`}
                  >
                    {track.subtitle}
                  </p>
                  {track.date && (
                    <span
                      className={`block text-[11px] font-medium${
                        isActive ? "text-white" : "text-green-04"
                      }`}
                    >
                      {track.date}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex gap-1 text-xs xl:text-sm text-green-04 font-medium pl-2">
                {track.durationLabel && <p>min</p>}
                <span>{track.durationLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
